<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useNavigationModel } from "@/composables/useNavigationModel";
import { useTicketStore } from "@/stores/TicketStore";
import { useVacancyStore } from "@/stores/VacancyStore";
import * as Icon from "@heroicons/vue/24/solid";

const props = defineProps({
  compact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close", "toggle-compact"]);

const { visibleAreas, currentAreaKey } = useNavigationModel();
const ticketStore = useTicketStore();
const vacancyStore = useVacancyStore();

const isDesktopViewport = ref(false);
let desktopMediaQuery;

const isCompactMode = computed(() => props.compact && isDesktopViewport.value);

const areaLinkClasses = (area) => {
  const active = area.key === currentAreaKey.value;
  const base =
    "relative group flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500";
  const activeClasses =
    "bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200 dark:bg-cyan-500/20 dark:text-cyan-100 dark:ring-cyan-500/30";
  const inactiveClasses =
    "text-slate-700 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-700";
  const compactClasses = isCompactMode.value
    ? "justify-center px-0 md:mx-auto md:h-12 md:w-12"
    : "";

  return `${base} ${active ? activeClasses : inactiveClasses} ${compactClasses}`;
};

const areaIconClasses = (area) => {
  const base = area.key === currentAreaKey.value ? "text-cyan-600 dark:text-cyan-200" : "text-slate-500 dark:text-slate-300";
  return isCompactMode.value ? `${base} h-6 w-6` : `${base} h-5 w-5`;
};

const areaBadge = (areaKey) => {
  if (areaKey === "customers") {
    const ticketCount = Number(ticketStore.unansweredCount ?? 0);
    return ticketCount > 0 ? (ticketCount > 99 ? "99+" : ticketCount) : null;
  }

  if (areaKey === "people") {
    const vacancyCount = Number(vacancyStore.newCount ?? 0);
    return vacancyCount > 0 ? (vacancyCount > 99 ? "99+" : vacancyCount) : null;
  }

  return null;
};

const isAreaVisible = (areaKey) =>
  visibleAreas.value.some((area) => area.key === areaKey);

onMounted(() => {
  if (typeof window !== "undefined") {
    desktopMediaQuery = window.matchMedia("(min-width: 768px)");
    isDesktopViewport.value = desktopMediaQuery.matches;

    if (typeof desktopMediaQuery.addEventListener === "function") {
      desktopMediaQuery.addEventListener("change", syncDesktopViewport);
    } else {
      desktopMediaQuery.addListener(syncDesktopViewport);
    }
  }

  if (isAreaVisible("customers")) {
    ticketStore.fetchUnansweredCount();
  }

  if (isAreaVisible("people")) {
    vacancyStore.fetchNewCount();
  }
});

onBeforeUnmount(() => {
  if (!desktopMediaQuery) {
    return;
  }

  if (typeof desktopMediaQuery.removeEventListener === "function") {
    desktopMediaQuery.removeEventListener("change", syncDesktopViewport);
  } else {
    desktopMediaQuery.removeListener(syncDesktopViewport);
  }
});

function syncDesktopViewport(event) {
  isDesktopViewport.value = event?.matches ?? desktopMediaQuery?.matches ?? false;
}
</script>

<template>
  <aside class="w-full">
    <div
      class="-mx-3 sticky top-0 z-10 mb-4 flex items-center gap-2 border-b border-slate-200 bg-slate-100/95 px-3 pb-3 pt-1 backdrop-blur dark:border-slate-700 dark:bg-slate-800/95 md:static md:mx-0 md:mb-3 md:border-0 md:bg-transparent md:px-0 md:pb-2 md:pt-0 md:backdrop-blur-0"
    >
      <div class="min-w-0 flex-1">
        <p v-if="!isCompactMode" class="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
          Navigation
        </p>
        <p v-if="!isCompactMode" class="text-sm font-semibold text-slate-800 dark:text-slate-100">
          Primary Areas
        </p>
      </div>
      <button
        type="button"
        class="hidden items-center justify-center rounded-md border border-slate-200 bg-white/70 p-2 text-slate-700 shadow-sm hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 md:inline-flex"
        :aria-label="isCompactMode ? 'Expand navigation' : 'Collapse navigation'"
        :title="isCompactMode ? 'Expand navigation' : 'Collapse navigation'"
        @click="emit('toggle-compact')"
      >
        <Icon.ChevronDoubleRightIcon v-if="isCompactMode" class="h-4 w-4" />
        <Icon.ChevronDoubleLeftIcon v-else class="h-4 w-4" />
      </button>
      <button
        type="button"
        class="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white/70 p-2 text-slate-700 shadow-sm hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 md:hidden"
        aria-label="Close navigation"
        @click="emit('close')"
      >
        <span class="text-lg leading-none">×</span>
      </button>
    </div>

    <nav aria-label="Primary navigation" class="flex flex-col gap-2 pb-3">
      <router-link
        v-for="area in visibleAreas"
        :key="area.key"
        :to="area.defaultLink || area.items[0]?.link || '/calendar'"
        :class="areaLinkClasses(area)"
        :title="isCompactMode ? area.label : `${area.label} - ${area.description}`"
        @click="emit('close')"
      >
        <component :is="area.icon" :class="`${areaIconClasses(area)} shrink-0`" />
        <template v-if="!isCompactMode">
          <div class="min-w-0 flex-1">
            <div class="truncate">{{ area.label }}</div>
            <div class="truncate text-xs text-slate-500 dark:text-slate-400">
              {{ area.description }}
            </div>
          </div>
          <span
            v-if="areaBadge(area.key)"
            class="ml-auto inline-flex min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-rose-500 px-2 py-0.5 text-xs font-semibold text-white"
          >
            {{ areaBadge(area.key) }}
          </span>
        </template>
        <span
          v-else-if="areaBadge(area.key)"
          class="absolute -right-1 -top-1 inline-flex min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-semibold text-white ring-2 ring-slate-100 dark:ring-slate-800"
        >
          {{ areaBadge(area.key) }}
        </span>
      </router-link>
    </nav>
  </aside>
</template>
